/**
 * Purchase order — shared types, the standing form text, and the printed
 * layout (company PO form, FORM NO PU/FM/02).  Board, raw material and spare
 * parts POs all print on it.
 *
 * Kept free of html2pdf so the PO screens can import it without pulling the
 * PDF library into the main bundle; generateBoardPoPdf.ts lazy-loads that.
 */

export const BOARD_PO_UOMS = ['SHEETS', 'KG', 'PKT', 'TON', 'NOS'] as const;
export type BoardPoUom = typeof BOARD_PO_UOMS[number];

/** Pre-filled on every new PO; editable per PO. */
export const BOARD_PO_DEFAULT_TERMS = [
  'Payment term 60 days',
  'Packing ensure safe delivery of goods',
  'Transit insurance included in cost',
  'Delay of delivery may lead to cancel the PO',
  'Please send PO copy with invoice',
  'Extra quantity will not be considered',
  'COA is required with delivery',
].join('\n');

export const BOARD_PO_DEFAULT_DELIVERY = 'DERABASSI';

export const COMPANY_NAME = 'VASUDEV PRINT PACK PRIVATE LIMITED';
export const COMPANY_ADDRESS = [
  'Hansa Industrial Park, Vill. Rampur Sainian. Derabassi',
  'Distt. SAS Nagar, Punjab',
  'Pin-Code-140507',
];
export const COMPANY_GSTIN = '03AAHCV7789D1Z0';
const FORM_NO = 'FORM NO PU/FM/02';

export interface BoardPoDocLine {
  description: string;
  qty: number;
  uom: string;
  rate: number;
  gstPercent: number | null;
  /** Board lines: the board, for supplier forms that print size and GSM. */
  board?: { paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null; brand?: string } | null;
  /** A supplier form's own columns for this line (Century: reelSheet, grain, discount, remarks). */
  formFields?: Record<string, string>;
}

/** Everything the printed PO needs. */
export interface BoardPoDoc {
  /** BOARD / RAW_MATERIAL / SPARE_PARTS - supplier forms are for board POs. */
  kind?: string;
  poNumber: string;
  poDate: string | null;
  vendorName: string;
  supplierAddress: string | null;
  deliveryLocation?: string | null;
  /** One term per line; blank prints the standard terms. */
  terms?: string | null;
  remarks?: string | null;
  lines: BoardPoDocLine[];
}

export function lineAmounts(l: { qty: number; rate: number; gstPercent: number | null }) {
  const amount = (Number(l.qty) || 0) * (Number(l.rate) || 0);
  const gstAmount = amount * (l.gstPercent == null ? 18 : Number(l.gstPercent)) / 100;
  return { amount, gstAmount, total: amount + gstAmount };
}

/**
 * Rupees for a weight (kg, 2 dp) at a rate (per kg, 4 dp), worked in whole
 * paise so a half paisa always rounds up — the same as the API's kgValue, so
 * a form's Value matches what gets saved and shown on the dashboard.
 */
export function kgValue(weightKg: number, ratePerKg: number): number {
  return Math.round((Math.round(weightKg * 100) * Math.round(ratePerKg * 10000)) / 10000) / 100;
}

/** kg per sheet from L × W (inch) × GSM; 1 in² = 0.00064516 m². 0 if unknown. */
export function sheetKg(b: { lengthIn: number | null; widthIn: number | null; gsm: number | null } | null): number {
  return b?.lengthIn && b.widthIn && b.gsm ? (b.lengthIn * b.widthIn * b.gsm * 0.00064516) / 1000 : 0;
}

/**
 * Rupees for a quantity (up to 4 dp) at a rate (4 dp), to the paisa, worked in
 * whole units so a half paisa always rounds up — the same sum as the API's
 * qtyValue, so a receipt form's Value is what gets saved.
 */
export function qtyValue(qty: number, rate: number): number {
  return Math.round((Math.round(qty * 10000) * Math.round(rate * 10000)) / 1_000_000) / 100;
}

/** 12,34,567.00 */
export function money(n: number): string {
  return (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 12,34,567 or 1,234.5 — quantities keep their decimals, if any. */
export function qtyText(n: number): string {
  return (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
}

/** 26 Sep 2026 */
export function poDateText(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Rupees in words, Indian grouping (lakh / crore), paise included when
 * present: 1,23,456.50 → "One Lakh Twenty Three Thousand Four Hundred Fifty
 * Six Rupees and Fifty Paise Only".
 */
export function amountInWords(n: number): string {
  const total = Math.round((Number(n) || 0) * 100);
  const rupees = Math.floor(total / 100);
  const paise = total % 100;
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x: number): string => {
    if (x < 20) return ones[x] ?? '';
    const o = x % 10;
    return `${tens[Math.floor(x / 10)]}${o ? ` ${ones[o]}` : ''}`;
  };
  const three = (x: number): string => {
    const h = Math.floor(x / 100);
    const r = x % 100;
    return [h ? `${ones[h]} Hundred` : '', r ? two(r) : ''].filter(Boolean).join(' ');
  };
  const words = (x: number): string => {
    if (x === 0) return 'Zero';
    const parts: string[] = [];
    const crore = Math.floor(x / 10_000_000);
    const lakh = Math.floor((x % 10_000_000) / 100_000);
    const thousand = Math.floor((x % 100_000) / 1000);
    const rest = x % 1000;
    // Past 99 crore the crore figure itself needs grouping.
    if (crore) parts.push(`${crore > 99 ? words(crore) : two(crore)} Crore`);
    if (lakh) parts.push(`${two(lakh)} Lakh`);
    if (thousand) parts.push(`${two(thousand)} Thousand`);
    if (rest) parts.push(three(rest));
    return parts.join(' ');
  };
  return `${words(rupees)} Rupees${paise ? ` and ${two(paise)} Paise` : ''} Only`;
}

function esc(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** The PO as one self-contained block of HTML (inline styles only). */
export function buildBoardPoHtml(po: BoardPoDoc): string {
  const totals = po.lines.map(lineAmounts);
  const grand = totals.reduce((s, t) => s + t.total, 0);
  const terms = (po.terms?.trim() ? po.terms : BOARD_PO_DEFAULT_TERMS)
    .split('\n').map((t) => t.trim()).filter(Boolean);
  // Only the real lines — no blank filler rows.
  const bodyRows = po.lines.map((l, i) => {
    const t = totals[i]!;
    return `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${esc(l.description)}</td>
        <td class="r">${qtyText(l.qty)}</td>
        <td class="c">${esc(l.uom)}</td>
        <td class="r">${money(l.rate)}</td>
        <td class="r">${money(t.amount)}</td>
        <td class="c">${l.gstPercent ?? 18}%</td>
        <td class="r">${money(t.gstAmount)}</td>
        <td class="r">${money(t.total)}</td>
      </tr>`;
  }).join('');

  return `
  <div style="box-sizing:border-box;font-family:'Times New Roman',Times,serif;color:#000;font-size:11px;width:100%;">
    <style>
      .po-tbl { border-collapse: collapse; width: 100%; }
      .po-tbl th, .po-tbl td { border: 1px solid #000; padding: 3px 5px; vertical-align: top; }
      .po-tbl th { font-weight: bold; text-align: center; background: #f2f2f2; }
      .po-tbl td.c { text-align: center; }
      .po-tbl td.r { text-align: right; white-space: nowrap; }
    </style>
    <table class="po-tbl">
      <tr>
        <td colspan="9" style="padding:0;text-align:center;">
          <div style="font-weight:bold;font-size:15px;padding:6px 6px 2px;">${COMPANY_NAME}</div>
          <div style="font-size:10.5px;line-height:1.35;">${COMPANY_ADDRESS.map(esc).join('<br>')}</div>
          <div style="font-size:10.5px;padding:2px 0 6px;">GSTIN : ${COMPANY_GSTIN}</div>
          <div style="font-weight:bold;font-size:13px;border-top:1px solid #000;padding:4px;letter-spacing:0.05em;">PURCHASE ORDER</div>
        </td>
      </tr>
      <tr>
        <td colspan="5" style="height:88px;">
          <div style="font-weight:bold;">Supplier Name and Address :</div>
          <div style="margin-top:4px;font-weight:bold;">${esc(po.vendorName)}</div>
          <div style="white-space:pre-line;">${esc(po.supplierAddress)}</div>
        </td>
        <td colspan="4">
          <table style="border-collapse:collapse;width:100%;">
            <tr><td style="border:none;padding:1px 0;width:34%;"><b>PO No.</b></td><td style="border:none;padding:1px 0;">: ${esc(po.poNumber)}</td></tr>
            <tr><td style="border:none;padding:1px 0;"><b>PO Date</b></td><td style="border:none;padding:1px 0;">: ${esc(poDateText(po.poDate))}</td></tr>
            <tr><td style="border:none;padding:1px 0;"><b>Version</b></td><td style="border:none;padding:1px 0;">: ${FORM_NO}</td></tr>
          </table>
        </td>
      </tr>
      <tr><td colspan="9" style="text-align:center;font-weight:bold;">Detail of PO</td></tr>
      <tr>
        <th style="width:5%;">S No.</th>
        <th style="width:31%;">Description</th>
        <th style="width:8%;">Qty.</th>
        <th style="width:6%;">UOM</th>
        <th style="width:9%;">Rate</th>
        <th style="width:11%;">Amount</th>
        <th style="width:5%;">GST</th>
        <th style="width:11%;">GST Amt.</th>
        <th style="width:14%;">Total Amt.</th>
      </tr>
      ${bodyRows}
      <tr>
        <td colspan="5"><b>Amt in word :</b> <i>${esc(amountInWords(grand))}</i></td>
        <td colspan="3" style="text-align:right;"><b>Total Value :</b></td>
        <td class="r"><b>${money(grand)}</b></td>
      </tr>
      ${po.remarks?.trim() ? `<tr><td colspan="9"><b>Remarks :</b> ${esc(po.remarks)}</td></tr>` : ''}
      <tr>
        <td colspan="9">
          <div><b>Terms and Conditions :</b></div>
          <div style="margin-top:3px;line-height:1.5;">${terms.map((t) => `<div>${esc(t)}</div>`).join('')}</div>
          <div style="margin-top:8px;"><b>Delivery Location :</b></div>
          <div>${esc(po.deliveryLocation?.trim() || BOARD_PO_DEFAULT_DELIVERY)}</div>
        </td>
      </tr>
    </table>
  </div>`;
}

/**
 * Open the PO in a new window and bring up the print dialog - on the company
 * PO form, or on a supplier's own form (lib/vendorPoFormats) when given one.
 */
export function printBoardPo(po: BoardPoDoc, form?: { build: (po: BoardPoDoc) => string; orientation: 'portrait' | 'landscape' }): void {
  const w = window.open('', '_blank', 'width=1100,height=900');
  if (!w) throw new Error('The print window was blocked — allow pop-ups for this site and try again.');
  const html = form ? form.build(po) : buildBoardPoHtml(po);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(po.poNumber)}</title>
    <style>@page { size: A4 ${form?.orientation ?? 'portrait'}; margin: 8mm; } body { margin: 0; }</style></head>
    <body>${html}</body></html>`);
  w.document.close();
  w.addEventListener('afterprint', () => w.close());
  // Give the new window a beat to lay out before the dialog snapshots it.
  setTimeout(() => { w.focus(); w.print(); }, 300);
}
